import React from "react";

export type ConnectorLineDirection = "in" | "out";

export type ConnectorLineColor =
    | "accent"
    | "line-base"
    | "foreground-base"
    | "ember"
    | (string & {});

export interface ConnectorLineProps {
    /**
     * `"in"` draws a 1px vertical line from the top down. `"out"`
     * retracts the line upward (top clips progressively, leaving the
     * bottom visible until the very end).
     *
     * Caller controls when to mount / unmount via this prop changing
     * — the keyframes replay each time direction changes (the line
     * remounts via key).
     */
    direction: ConnectorLineDirection;
    /**
     * Edge color. Maps to a Solomon color token (accent / line-base /
     * foreground-base / ember). Pass any other CSS color value
     * (`#fd4017`, `rgb(var(--color-...))`) and it's used verbatim.
     */
    color?: ConnectorLineColor;
    /**
     * Animation duration in ms. Defaults: 75ms in (snappy drop),
     * 200ms out (deliberate retract). Override per direction by
     * passing `duration` when the caller knows which direction
     * they're rendering.
     */
    duration?: number;
}

function resolveColor(color: ConnectorLineColor): string {
    switch (color) {
        case "accent":
            return "rgb(var(--color-accent-base))";
        case "line-base":
            return "rgb(var(--color-line-base))";
        case "foreground-base":
            return "rgb(var(--color-foreground-base))";
        case "ember":
            return "rgb(var(--color-ember-500))";
        default:
            return color;
    }
}

const KEYFRAMES_CSS = `
@keyframes connector-line-in {
    0%   { clip-path: inset(0 0 100% 0); }
    100% { clip-path: inset(0 0 0 0); }
}
@keyframes connector-line-out {
    0%   { clip-path: inset(0 0 0 0); }
    100% { clip-path: inset(100% 0 0 0); }
}
`;

const DEFAULT_IN_DURATION = 75;
const DEFAULT_OUT_DURATION = 200;

/**
 * 1px vertical connector line — drops in or retracts out via
 * clip-path. Self-injects keyframes; takes 100% height of its
 * parent. Pair with a flex-positioned wrapper to drop it between
 * two cards (typically with negative top/bottom margins so it sits
 * inside the gap rather than displacing layout):
 *
 * ```tsx
 * {direction && (
 *     <div className="flex justify-center"
 *          style={{ height: 16, marginTop: -16, marginBottom: -16 }}>
 *         <ConnectorLine direction={direction} color="accent" />
 *     </div>
 * )}
 * ```
 */
export function ConnectorLine({
    direction,
    color = "accent",
    duration
}: ConnectorLineProps): React.ReactElement {
    const edgeColor = resolveColor(color);
    const resolvedDuration =
        duration ??
        (direction === "in" ? DEFAULT_IN_DURATION : DEFAULT_OUT_DURATION);
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }} />
            <div
                key={direction}
                className="w-px h-full"
                style={{
                    background: edgeColor,
                    animation: `connector-line-${direction} ${resolvedDuration}ms ease-out forwards`
                }}
            />
        </>
    );
}
